/**
 * Performance Monitoring Utilities
 * Tracks Core Web Vitals and provides performance insights
 */

import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

// Performance thresholds based on Google's recommendations
const PERFORMANCE_THRESHOLDS = {
  LCP: { good: 2500, needsImprovement: 4000 }, // ms
  FID: { good: 100, needsImprovement: 300 },   // ms
  CLS: { good: 0.1, needsImprovement: 0.25 },  // score
  FCP: { good: 1800, needsImprovement: 3000 }, // ms
  TTFB: { good: 800, needsImprovement: 1800 }  // ms
};

/**
 * Get performance rating based on thresholds
 */
function getPerformanceRating(metric, value) {
  const threshold = PERFORMANCE_THRESHOLDS[metric];
  if (!threshold) return 'unknown';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.needsImprovement) return 'needs-improvement';
  return 'poor';
}

/**
 * Send performance data to analytics or logging service
 */
function sendToAnalytics(metric) {
  const rating = getPerformanceRating(metric.name, metric.value);
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`Performance Metric: ${metric.name}`, {
      value: metric.value,
      rating,
      id: metric.id,
      delta: metric.delta
    });
  }
  
  // Send to Google Analytics if available
  if (window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      event_label: rating,
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      non_interaction: true,
    });
  }
  
  // You can also send to other analytics services here
  // Example: send to your backend for monitoring
  // fetch('/api/performance-metrics', {
  //   method: 'POST',
  //   body: JSON.stringify({ ...metric, rating }),
  //   headers: { 'Content-Type': 'application/json' }
  // });
}

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring() {
  // Track all Core Web Vitals
  getCLS(sendToAnalytics);
  getFID(sendToAnalytics);
  getFCP(sendToAnalytics);
  getLCP(sendToAnalytics);
  getTTFB(sendToAnalytics);
  
  // Additional performance tracking
  if ('PerformanceObserver' in window) {
    // Track Long Tasks (> 50ms)
    try {
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 50) {
            console.warn(`Long Task detected: ${entry.duration}ms`, entry);
            
            if (window.gtag) {
              window.gtag('event', 'long_task', {
                event_category: 'Performance',
                value: Math.round(entry.duration),
                non_interaction: true,
              });
            }
          }
        });
      });
      
      longTaskObserver.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      // Long task observer not supported
    }
    
    // Track Resource Loading Performance
    try {
      const resourceObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          // Track slow image loads
          if (entry.initiatorType === 'img' && entry.duration > 1000) {
            console.warn(`Slow image load: ${entry.name} took ${entry.duration}ms`);
          }
          
          // Track large resources
          if (entry.transferSize > 1024 * 1024) { // > 1MB
            console.warn(`Large resource: ${entry.name} is ${Math.round(entry.transferSize / 1024)}KB`);
          }
        });
      });
      
      resourceObserver.observe({ entryTypes: ['resource'] });
    } catch (e) {
      // Resource observer not supported
    }
  }
}

/**
 * Get current performance metrics
 */
export function getCurrentPerformanceMetrics() {
  const navigation = performance.getEntriesByType('navigation')[0];
  
  if (!navigation) return null;
  
  return {
    // Navigation timing
    domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
    loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
    
    // Network timing
    dns: navigation.domainLookupEnd - navigation.domainLookupStart,
    tcp: navigation.connectEnd - navigation.connectStart,
    request: navigation.responseStart - navigation.requestStart,
    response: navigation.responseEnd - navigation.responseStart,
    
    // Processing timing
    domProcessing: navigation.domComplete - navigation.domLoading,
    
    // Total page load time
    totalLoadTime: navigation.loadEventEnd - navigation.fetchStart
  };
}

/**
 * Monitor bundle size and warn if too large
 */
export function monitorBundleSize() {
  if ('performance' in window) {
    const resources = performance.getEntriesByType('resource');
    let totalJSSize = 0;
    let totalCSSSize = 0;
    
    resources.forEach(resource => {
      if (resource.name.includes('.js')) {
        totalJSSize += resource.transferSize || 0;
      } else if (resource.name.includes('.css')) {
        totalCSSSize += resource.transferSize || 0;
      }
    });
    
    const totalSize = totalJSSize + totalCSSSize;
    
    // Warn if bundle is too large
    if (totalSize > 500 * 1024) { // > 500KB
      console.warn(`Large bundle size detected: ${Math.round(totalSize / 1024)}KB total`);
      console.log(`JS: ${Math.round(totalJSSize / 1024)}KB, CSS: ${Math.round(totalCSSSize / 1024)}KB`);
    }
    
    return {
      totalJSSize,
      totalCSSSize,
      totalSize
    };
  }
  
  return null;
}

const performanceMonitoring = {
  initPerformanceMonitoring,
  getCurrentPerformanceMetrics,
  monitorBundleSize,
  PERFORMANCE_THRESHOLDS
};

export default performanceMonitoring;
